
import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PricingProps {
  onContact: () => void;
}

/**
 * AnimatedCheck component displays a single bullet point with a check icon.
 * It uses IntersectionObserver to trigger a fade‑in/slide‑in animation when
 * the element enters the viewport.
 */
function AnimatedCheck({ text, index }: { text: string; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Guard against browsers that do not support IntersectionObserver
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -30px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${index * 200}ms` }}
      className={`flex items-center justify-center gap-2 sm:gap-3 mb-3 sm:mb-4 transition-all duration-700
        ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
    >
      <div
        style={{ transitionDelay: `${index * 200 + 300}ms` }}
        className={`w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center bg-[#03912c] rounded-full flex-shrink-0 transition-all duration-500
          ${visible ? 'scale-100' : 'scale-0'}`}
      >
        <i className="ri-check-line text-white text-xs sm:text-sm" aria-hidden="true"></i>
      </div>
      <p className="text-sm sm:text-base text-[#333333] font-medium">{text}</p>
    </div>
  );
}

/**
 * Pricing component renders the pricing section with a background image,
 * title, description, animated bullet points and a call‑to‑action button.
 */
export default function Pricing({ onContact }: PricingProps) {
  const { t } = useTranslation();

  // Collect translation strings safely; fallback to an empty string if missing
  const keyPoints = [
    t('pricing.p1') ?? '',
    t('pricing.p2') ?? '',
    t('pricing.p3') ?? '',
  ];

  return (
    <section id="soluciones" className="py-14 sm:py-24 relative overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            'url(https://static.readdy.ai/image/d4364c93ef6f707650170405d0b37024/143fbe7ace9c796c3f7c376f7504248f.jpeg)',
        }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-white/85" aria-hidden="true" />

      <div className="max-w-4xl mx-auto px-5 sm:px-6 text-center relative z-10">
        <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-4 sm:mb-6">
          {t('pricing.title')}
        </h2>
        <p className="text-sm sm:text-lg text-[#666666] mb-8 sm:mb-10 leading-relaxed">
          {t('pricing.desc')}
        </p>

        <div className="mb-8 sm:mb-10">
          {keyPoints.map((point, index) => (
            <AnimatedCheck key={index} text={point} index={index} />
          ))}
        </div>

        <button
          onClick={onContact}
          className="inline-flex items-center bg-[#d6df00] text-[#1A1A1A] px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-medium hover:bg-[#c2cb00] transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
        >
          {t('pricing.cta')}
          <i className="ri-arrow-right-line ml-2" aria-hidden="true"></i>
        </button>
      </div>
    </section>
  );
}
